import React, { useContext, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  useTheme,
} from "@mui/material";
import clsx from "clsx";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import mainStyles from "../../../styles";
import { fillOrgWithDemoManufacturingData } from "api/endpoints/manufacturing";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import DisplayHeading from "components/shared/display-heading";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import { PermissionsContext } from "context/permissions-context";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

// The OEE dashboard offers windows up to 30 days and compares each against
// another window of the same length, so 60 days of history is what fills every
// figure it can show. Kept in step with the validator on
// FillOrgWithDemoManufacturingData, which rejects anything above it.
const MAX_DAYS_IN_PAST = 60;

// Default to the full range: seeding less leaves the longer windows reading as
// mostly-empty days, which is the impression a demo is meant to avoid.
const DEFAULT_DAYS_IN_PAST = MAX_DAYS_IN_PAST;

interface OrgManufacturingTabProps {
  roleId: string;
  regionCode: string;
}

const OrgManufacturingTab = ({
  roleId,
  regionCode,
}: OrgManufacturingTabProps) => {
  const { showError } = useContext(SnackbarContext);
  const { hasPermissions, permissions } = useContext(PermissionsContext);

  const classes = mainStyles();
  const theme = useTheme();

  const [daysInPast, setDaysInPast] = useState(DEFAULT_DAYS_IN_PAST);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const canFillDemoManufacturingData = hasPermissions([
    ActionType.FILL_ORG_WITH_DEMO_MANUFACTURING_DATA,
  ]);

  const handleConfirm = async () => {
    if (!canFillDemoManufacturingData) {
      return;
    }

    setIsLoading(true);
    fillOrgWithDemoManufacturingData(
      { orgId: roleId, daysInPast },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((response) => {
        setSuccessMessage(
          `Demo manufacturing data filled successfully across ${response.targetedLineCount} production line(s).`,
        );
      })
      .catch((err) => {
        showError(err as GrpcError);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Grid container>
      {permissions.isPermissionsLoading && (
        <Grid item xs={12} sx={{ paddingTop: 2 }}>
          <CircularProgress size={24} />
        </Grid>
      )}
      <Grid item xs={12} sx={classes.paddingTopSmall}>
        <DisplayHeading>Manufacturing</DisplayHeading>
      </Grid>
      <Grid item xs={12}>
        <Box
          style={{
            borderColor: theme.palette.divider,
            borderWidth: "1px",
            borderStyle: "solid",
            borderRadius: "5px",
            margin: "20px",
          }}
        >
          <Table size="medium">
            <TableBody>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                  style={{ fontWeight: "normal", width: "60%" }}
                >
                  {!permissions.isPermissionsLoading &&
                  !canFillDemoManufacturingData ? (
                    "You do not have permission to fill org with demo manufacturing data."
                  ) : (
                    <>
                      <strong>Fill with demo data</strong>
                      <br />
                      Fill this organisation with synthetic manufacturing demo
                      data. Production lines must already exist in the org.
                    </>
                  )}
                </TableCell>
                <TableCell
                  className={clsx(classes.tableCell, classes.rightButtonCell)}
                  align="right"
                >
                  <TextField
                    label="Days of history"
                    type="number"
                    value={daysInPast}
                    onChange={(e) => {
                      const val = Number.parseInt(e.target.value, 10);
                      setDaysInPast(
                        Number.isNaN(val)
                          ? 0
                          : Math.min(MAX_DAYS_IN_PAST, Math.max(0, val)),
                      );
                    }}
                    inputProps={{ min: 0, max: MAX_DAYS_IN_PAST }}
                    size="small"
                    helperText={`Number of days of historical data to backfill (0–${MAX_DAYS_IN_PAST})`}
                  />
                  <Box sx={{ marginTop: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={
                        isLoading ||
                        permissions.isPermissionsLoading ||
                        !canFillDemoManufacturingData
                      }
                      onClick={() => {
                        if (canFillDemoManufacturingData) {
                          setIsConfirmOpen(true);
                        }
                      }}
                      data-anchor="manufacturing-fill-demo-data-button"
                      fullWidth
                    >
                      Fill with demo data
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Grid>

      <ConfirmDialog
        title="Fill org with demo manufacturing data"
        open={isConfirmOpen}
        setOpen={setIsConfirmOpen}
        onConfirm={handleConfirm}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
      >
        This will generate synthetic production runs, check results, and related
        activity for <strong>{daysInPast}</strong> day(s) across all existing
        production lines in this organisation. Are you sure?
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={!!successMessage}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setSuccessMessage("")}
        data-testid="manufacturing-fill-demo-data-success-snackbar"
      >
        <Alert severity="success" variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default OrgManufacturingTab;
